import {
  getNotificationsAdminAcknowledgements,
  getNotificationsAdminBroadcasts,
  getNotificationsAdminDeliveries,
  getNotificationsAdminEscalations,
  getNotificationsAdminEvents,
  getNotificationsAdminNotifications,
  getNotificationsAdminTemplates,
  getNotificationsAdminTemplatesByCode,
  postNotificationsAdminBroadcasts,
  postNotificationsAdminTemplates,
} from '@/generated/openapi'
import type {
  GetNotificationsAdminAcknowledgementsData,
  GetNotificationsAdminBroadcastsData,
  GetNotificationsAdminDeliveriesData,
  GetNotificationsAdminEscalationsData,
  GetNotificationsAdminEventsData,
  GetNotificationsAdminNotificationsData,
  GetNotificationsAdminTemplatesData,
  GetNotificationsAdminTemplatesByCodeData,
  PostNotificationsAdminBroadcastsData,
  PostNotificationsAdminTemplatesData,
} from '@/generated/openapi'
import { ApiError } from '@/services/apiErrors'

interface GeneratedResult {
  data?: unknown
  error?: unknown
  response?: Response
}

interface BackendError {
  code?: string
  message?: string
  details?: unknown
}

interface BackendEnvelope {
  success?: boolean
  failure?: boolean
  status?: string
  data?: unknown
  payload?: unknown
  error?: string | BackendError
  error_code?: string
}

export type NotificationTemplateChannel = 'in_app' | 'push' | 'email'

export interface NotificationTemplateRecord {
  id: string
  code: string
  version: number
  locale?: string
  channel: NotificationTemplateChannel
  subject_template?: string
  body_template: string
  metadata?: Record<string, unknown> | null
  active: boolean
  created_at: string
  updated_at: string
}

export interface NotificationTemplateUpsertRequest {
  code: string
  version: number
  locale?: string
  channel: NotificationTemplateChannel
  subject_template?: string
  body_template: string
  metadata?: Record<string, unknown> | null
  active: boolean
}

export interface NotificationTemplateListParams {
  code?: string
  limit?: number
}

export type NotificationBroadcastTargetGroup = 'all_admins' | 'all_pilots' | 'all_partners' | 'all_users'
export type NotificationBroadcastPriority = 'low' | 'normal' | 'high' | 'urgent'

export interface NotificationBroadcastRequest {
  target_group: NotificationBroadcastTargetGroup
  title: string
  body: string
  priority: NotificationBroadcastPriority
  requires_acknowledgement: boolean
}

export interface NotificationBroadcastListParams {
  limit?: number
  since?: string
  before?: string
}

export interface NotificationGenerationResult {
  event_id: string
  event_type: string
  policy_template_code: string
  recipients?: string[]
  notification_ids?: string[]
  delivery_ids?: string[]
  escalation_ids?: string[]
  skipped_recipients?: string[]
  notifications_created: number
  deliveries_created: number
}

export interface NotificationBroadcastResult {
  broadcast_id: string
  event_id: string
  event_type: string
  template_code: string
  target_group: NotificationBroadcastTargetGroup
  title: string
  body: string
  priority: NotificationBroadcastPriority
  requires_acknowledgement: boolean
  created_by_user_id: string
  created_by_username?: string
  generation?: NotificationGenerationResult
}

export type NotificationBroadcastListStatus = 'delivered' | 'partial_failure' | 'failed' | 'pending'

export interface NotificationBroadcastListSummary {
  broadcast_count: number
  delivered_count: number
  partial_failure_count: number
  failed_count: number
  pending_count: number
}

export interface NotificationBroadcastListItem {
  broadcast_id: string
  event_id: string
  sent_at: string
  title: string
  recipient_group: string
  channels: NotificationDeliveryChannel[]
  status: NotificationBroadcastListStatus
  notification_count: number
  recipient_count: number
  delivery_count: number
  delivered_delivery_count: number
  pending_delivery_count: number
  failed_delivery_count: number
  priority: NotificationBroadcastPriority
  requires_acknowledgement: boolean
  created_by_user_id?: string
  created_by_username?: string
}

export interface NotificationBroadcastListResult {
  broadcasts: NotificationBroadcastListItem[]
  summary: NotificationBroadcastListSummary
  limit: number
  since?: string | null
  before?: string | null
}

export type NotificationEventCategory =
  | 'auth'
  | 'user'
  | 'aircraft'
  | 'flight'
  | 'document'
  | 'maintenance'
  | 'financial'
  | 'system'
  | 'safety'

export type NotificationEventProcessingStatus = 'pending' | 'processing' | 'processed' | 'failed' | 'skipped'

export interface NotificationEventRecord {
  id: string
  dedup_key: string
  event_type: string
  category: NotificationEventCategory
  aggregate_type: string
  aggregate_id: string
  payload?: Record<string, unknown> | null
  occurred_at: string
  received_at: string
  processing_status: NotificationEventProcessingStatus
  processing_error?: string
  created_at: string
  updated_at: string
}

export interface NotificationEventListParams {
  limit?: number
  since?: string
  before?: string
  event_type?: string
  category?: NotificationEventCategory
  processing_status?: NotificationEventProcessingStatus
}

export type NotificationRecordCategory = 'system' | 'flight' | 'safety' | 'support'
export type NotificationAdminCategory = NotificationRecordCategory

export interface NotificationRecord {
  id: string
  event_id?: string
  dedup_key: string
  recipient_user_id?: string
  recipient_reference?: string
  template_code: string
  template_version: number
  locale?: string
  params?: Record<string, unknown> | null
  priority: NotificationBroadcastPriority
  requires_acknowledgement: boolean
  ack_deadline_at?: string
  read_at?: string
  created_at: string
  updated_at: string
}

export type NotificationAdminRecord = NotificationRecord

export interface NotificationAdminListParams {
  limit?: number
  since?: string
  before?: string
  recipient_user_id?: string
  recipient_reference?: string
  template_code?: string
  category?: NotificationRecordCategory
  priority?: NotificationBroadcastPriority
  requires_acknowledgement?: boolean
  read?: boolean
}

export type NotificationDeliveryChannel = 'in_app' | 'push' | 'email'
export type NotificationDeliveryV1Status = 'pending' | 'scheduled' | 'delivered' | 'failed'

export interface NotificationDeliveryRecord {
  id: string
  notification_id?: string
  channel: NotificationDeliveryChannel
  status: NotificationDeliveryV1Status
  attempts: number
  last_attempt_at?: string
  sent_at?: string
  failed_at?: string
  error_message?: string
  provider_reference?: string
  created_at: string
  updated_at: string
}

export type NotificationAcknowledgementSource = 'ui' | 'api' | 'offline' | 'system'

export interface NotificationAcknowledgementRecord {
  id: string
  notification_id: string
  user_id: string
  acknowledged_at: string
  source: NotificationAcknowledgementSource
  comment?: string
  created_at: string
  updated_at: string
}

export interface NotificationDeliveryListParams {
  limit?: number
  since?: string
  before?: string
  notification_id?: string
  channel?: NotificationDeliveryChannel
  status?: NotificationDeliveryV1Status
}

export interface NotificationAcknowledgementListParams {
  limit?: number
  before?: string
  notification_id?: string
  user_id?: string
}

export type NotificationEscalationStatus = 'pending' | 'executed' | 'failed' | 'cancelled'

export interface NotificationEscalationRecord {
  id: string
  notification_id: string
  level: number
  trigger_at: string
  status: NotificationEscalationStatus
  target_user_id?: string
  target_group?: string
  executed_at?: string
  error_message?: string
  created_at: string
  updated_at: string
}

export interface NotificationEscalationListParams {
  limit?: number
  before?: string
  notification_id?: string
  status?: NotificationEscalationStatus
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function toBackendEnvelope(value: unknown): BackendEnvelope | undefined {
  if (!isRecord(value)) return undefined
  return value as BackendEnvelope
}

function extractBackendError(value: unknown, fallbackMessage: string): ApiError {
  const envelope = toBackendEnvelope(value)
  const error = envelope?.error

  if (typeof error === 'string') {
    return new ApiError(error, envelope?.error_code)
  }

  if (isRecord(error)) {
    const message = typeof error.message === 'string' ? error.message : fallbackMessage
    const code = typeof error.code === 'string' ? error.code : envelope?.error_code
    return new ApiError(message, code)
  }

  return new ApiError(envelope?.error_code ?? fallbackMessage, envelope?.error_code)
}

function readGeneratedEnvelope<T>(result: GeneratedResult, fallbackMessage: string): T {
  if (result.error !== undefined) {
    throw extractBackendError(result.error, fallbackMessage)
  }

  const envelope = toBackendEnvelope(result.data)
  if (!envelope) {
    if (result.response && !result.response.ok) throw new ApiError(fallbackMessage)
    return result.data as T
  }

  if (envelope.status === 'error' || envelope.failure === true || envelope.error) {
    throw extractBackendError(envelope, fallbackMessage)
  }

  if (envelope.status === 'success' || envelope.success === true || 'data' in envelope || 'payload' in envelope) {
    return (envelope.data ?? envelope.payload) as T
  }

  return envelope as T
}

function templateQuery(params?: NotificationTemplateListParams): GetNotificationsAdminTemplatesData['query'] {
  if (!params) return undefined
  return {
    code: params.code,
    limit: params.limit,
  }
}

function templatePath(code: string): GetNotificationsAdminTemplatesByCodeData['path'] {
  return { code }
}

function templateBody(payload: NotificationTemplateUpsertRequest): PostNotificationsAdminTemplatesData['body'] {
  const body: PostNotificationsAdminTemplatesData['body'] = {
    active: payload.active,
    body_template: payload.body_template,
    channel: payload.channel,
    code: payload.code,
    version: payload.version,
  }

  if (payload.locale !== undefined) body.locale = payload.locale
  if (payload.metadata != null) body.metadata = payload.metadata
  if (payload.subject_template !== undefined) body.subject_template = payload.subject_template

  return body
}

function broadcastBody(payload: NotificationBroadcastRequest): PostNotificationsAdminBroadcastsData['body'] {
  return {
    body: payload.body,
    priority: payload.priority,
    requires_acknowledgement: payload.requires_acknowledgement,
    target_group: payload.target_group,
    title: payload.title,
  }
}

function broadcastQuery(params?: NotificationBroadcastListParams): GetNotificationsAdminBroadcastsData['query'] {
  if (!params) return undefined
  return {
    before: params.before,
    limit: params.limit,
    since: params.since,
  }
}

function eventQuery(params?: NotificationEventListParams): GetNotificationsAdminEventsData['query'] {
  if (!params) return undefined
  return {
    before: params.before,
    category: params.category,
    event_type: params.event_type,
    limit: params.limit,
    processing_status: params.processing_status,
    since: params.since,
  }
}

function notificationQuery(params?: NotificationAdminListParams): GetNotificationsAdminNotificationsData['query'] {
  if (!params) return undefined
  return {
    before: params.before,
    category: params.category,
    limit: params.limit,
    priority: params.priority,
    read: params.read,
    recipient_reference: params.recipient_reference,
    recipient_user_id: params.recipient_user_id,
    requires_acknowledgement: params.requires_acknowledgement,
    since: params.since,
    template_code: params.template_code,
  }
}

function deliveryQuery(params?: NotificationDeliveryListParams): GetNotificationsAdminDeliveriesData['query'] {
  if (!params) return undefined
  return {
    before: params.before,
    channel: params.channel,
    limit: params.limit,
    notification_id: params.notification_id,
    since: params.since,
    status: params.status,
  }
}

function acknowledgementQuery(
  params?: NotificationAcknowledgementListParams,
): GetNotificationsAdminAcknowledgementsData['query'] {
  if (!params) return undefined
  return {
    before: params.before,
    limit: params.limit,
    notification_id: params.notification_id,
    user_id: params.user_id,
  }
}

function escalationQuery(params?: NotificationEscalationListParams): GetNotificationsAdminEscalationsData['query'] {
  if (!params) return undefined
  return {
    before: params.before,
    limit: params.limit,
    notification_id: params.notification_id,
    status: params.status,
  }
}

export const notificationAdminService = {
  async listTemplates(params?: NotificationTemplateListParams): Promise<NotificationTemplateRecord[]> {
    const data = readGeneratedEnvelope<{ templates?: NotificationTemplateRecord[] }>(
      await getNotificationsAdminTemplates({ query: templateQuery(params) }),
      'Falha ao carregar templates de notificação',
    )
    return data.templates ?? []
  },

  async listTemplateVersions(code: string): Promise<NotificationTemplateRecord[]> {
    const data = readGeneratedEnvelope<{ templates?: NotificationTemplateRecord[] }>(
      await getNotificationsAdminTemplatesByCode({ path: templatePath(code) }),
      'Falha ao carregar versões do template de notificação',
    )
    return data.templates ?? []
  },

  async upsertTemplate(payload: NotificationTemplateUpsertRequest): Promise<NotificationTemplateRecord> {
    return readGeneratedEnvelope<NotificationTemplateRecord>(
      await postNotificationsAdminTemplates({ body: templateBody(payload) }),
      'Falha ao salvar template de notificação',
    )
  },

  async createBroadcast(payload: NotificationBroadcastRequest): Promise<NotificationBroadcastResult> {
    return readGeneratedEnvelope<NotificationBroadcastResult>(
      await postNotificationsAdminBroadcasts({ body: broadcastBody(payload) }),
      'Falha ao criar broadcast de notificação',
    )
  },

  async listBroadcasts(params?: NotificationBroadcastListParams): Promise<NotificationBroadcastListResult> {
    const data = readGeneratedEnvelope<{
      broadcasts?: NotificationBroadcastListItem[] | null
      summary: NotificationBroadcastListSummary
      limit: number
      since?: string | null
      before?: string | null
    }>(
      await getNotificationsAdminBroadcasts({ query: broadcastQuery(params) }),
      'Falha ao carregar broadcasts recentes',
    )
    return {
      ...data,
      broadcasts: data.broadcasts ?? [],
    }
  },

  async listEvents(params?: NotificationEventListParams): Promise<NotificationEventRecord[]> {
    const data = readGeneratedEnvelope<{ events?: NotificationEventRecord[] | null }>(
      await getNotificationsAdminEvents({ query: eventQuery(params) }),
      'Falha ao carregar eventos de notificação',
    )
    return data.events ?? []
  },

  async listNotifications(params?: NotificationAdminListParams): Promise<NotificationRecord[]> {
    const data = readGeneratedEnvelope<{ notifications?: NotificationRecord[] | null }>(
      await getNotificationsAdminNotifications({ query: notificationQuery(params) }),
      'Falha ao carregar notificações geradas',
    )
    return data.notifications ?? []
  },

  async listDeliveries(params?: NotificationDeliveryListParams): Promise<NotificationDeliveryRecord[]> {
    const data = readGeneratedEnvelope<{ deliveries?: NotificationDeliveryRecord[] | null }>(
      await getNotificationsAdminDeliveries({ query: deliveryQuery(params) }),
      'Falha ao carregar entregas de notificação',
    )
    return data.deliveries ?? []
  },

  async listAcknowledgements(
    params?: NotificationAcknowledgementListParams,
  ): Promise<NotificationAcknowledgementRecord[]> {
    const data = readGeneratedEnvelope<{ acknowledgements?: NotificationAcknowledgementRecord[] | null }>(
      await getNotificationsAdminAcknowledgements({ query: acknowledgementQuery(params) }),
      'Falha ao carregar confirmações de notificação',
    )
    return data.acknowledgements ?? []
  },

  async listEscalations(params?: NotificationEscalationListParams): Promise<NotificationEscalationRecord[]> {
    const data = readGeneratedEnvelope<{ escalations?: NotificationEscalationRecord[] | null }>(
      await getNotificationsAdminEscalations({ query: escalationQuery(params) }),
      'Falha ao carregar escalonamentos de notificação',
    )
    return data.escalations ?? []
  },
}
